import { useState } from 'react'
import { formatAmount, formatDayTime, nowMs, pluralRu } from '@dorixona/utils'
import { env } from '../../config/env'
import { Icon } from '../../ui/Icon'
import { Failed, failureText } from '../../ui/Load'
import { Modal } from '../../ui/Modal'
import { useSession } from '../auth/session'
import {
  useFomStores,
  useIssueFomCredentials,
  type FomSecrets,
  type FomStore,
} from '../integrations/api'
import type { Store } from './api'
import { FOM_STOCK_PATH, fomHandoffText, fomStockStale } from './model'

export interface StoreFomProps {
  store: Store
  onToast: (message: string) => void
}

/**
 * Доступы аптеки для программ FOM: логин и пароль кассы (CASHBACK.exe) и
 * токен SITE.exe. Здесь их выдают и видят, доходят ли от аптеки остатки и чеки.
 *
 * Отдельной ручки по одной аптеке нет — строка берётся из общего списка
 * `GET /admin/integrations/fom` по `store_id`. Пароль и токен список не отдаёт
 * никогда: они приходят один раз в ответе на выдачу и живут только в окне.
 */
export function StoreFom({ store, onToast }: StoreFomProps) {
  const { can } = useSession()
  const canIssue = can('integrations', 'u')
  const fom = useFomStores()
  const issue = useIssueFomCredentials()
  const [confirming, setConfirming] = useState(false)
  const [secrets, setSecrets] = useState<FomSecrets | null>(null)

  const row = fom.data?.find((s) => s.storeId === store.id)
  const issued = Boolean(row?.login)

  const run = () => {
    issue.mutate(store.id, {
      onSuccess: (done) => {
        setSecrets(done)
      },
      onError: (error) => {
        onToast(failureText(error))
      },
      onSettled: () => {
        setConfirming(false)
      },
    })
  }

  const closeSecrets = () => {
    setSecrets(null)
    // Ответ с паролем держит и сама мутация — сбрасываем, чтобы не остался в памяти.
    issue.reset()
    // Пока окно было открыто, техник мог уже вписать доступы — перечитываем.
    void fom.refetch()
  }

  return (
    <div className="ui-stack st-solo">
      <section className="ui-card" aria-labelledby="st-fom">
        <div className="ui-card-head">
          <h3 id="st-fom">Доступы FOM</h3>
          {fom.data ? (
            issued ? (
              <span className="ui-status" data-tone="success">
                выданы
              </span>
            ) : (
              <span className="ui-status">не выданы</span>
            )
          ) : null}
          {canIssue && fom.data ? (
            <button
              type="button"
              className="ui-btn"
              data-variant={issued ? 'secondary' : undefined}
              data-size="sm"
              disabled={issue.isPending}
              aria-busy={issue.isPending}
              onClick={() => {
                if (issued) setConfirming(true)
                else run()
              }}
            >
              <Icon name={issued ? 'i-sync' : 'i-key'} />
              {issued ? 'Перевыпустить' : 'Выдать доступы'}
            </button>
          ) : null}
        </div>
        {fom.isPending ? (
          <div className="ui-card-body">
            <span className="ui-skeleton st-skel" />
          </div>
        ) : null}
        {fom.isError ? (
          <div className="ui-card-body">
            <Failed
              what="доступы FOM"
              error={fom.error}
              onRetry={() => {
                void fom.refetch()
              }}
            />
          </div>
        ) : null}
        {fom.data ? <FomFacts row={row} /> : null}
      </section>

      {confirming ? (
        <Modal
          title="Перевыпустить доступы?"
          onClose={() => {
            setConfirming(false)
          }}
        >
          <p>
            Старые логин, пароль и токен перестанут работать. Кассы этой аптеки выйдут из системы,
            пока техник FOM не впишет новые доступы.
          </p>
          <div className="mfoot">
            <button
              type="button"
              className="ui-btn"
              data-variant="secondary"
              onClick={() => {
                setConfirming(false)
              }}
            >
              Не перевыпускать
            </button>
            <button
              type="button"
              className="ui-btn"
              data-variant="danger"
              disabled={issue.isPending}
              aria-busy={issue.isPending}
              onClick={run}
            >
              Перевыпустить
            </button>
          </div>
        </Modal>
      ) : null}

      {secrets ? (
        <FomIssued
          storeName={store.name}
          secrets={secrets}
          onClose={closeSecrets}
          onToast={onToast}
        />
      ) : null}
    </div>
  )
}

/** Что известно о связи с аптекой: доступы, филиал, последние остатки и чек. */
function FomFacts({ row }: { row: FomStore | undefined }) {
  const lastStock = row?.lastStock ?? 0
  const rows = row?.lastStockRows ?? 0
  const stale = fomStockStale(lastStock, nowMs())

  return (
    <div className="ui-card-body st-facts-body">
      <div className="ui-facts">
        <div>
          <span className="k">Логин кассы</span>
          <span className="v">
            {row?.login ? <span className="ui-code">{row.login}</span> : 'Доступы не выданы'}
          </span>
        </div>
        <div>
          <span className="k">Выданы</span>
          <span className="v num">{row?.issued ? formatDayTime(row.issued) : '—'}</span>
        </div>
        <div>
          <span className="k">Филиал FOM</span>
          <span className="v">
            {row?.branch ? <span className="ui-code">{row.branch}</span> : 'ещё не приходил'}
          </span>
        </div>
        <div>
          <span className="k">Последние остатки</span>
          <span className="v num">
            {/* «Ещё не приходили» — по времени выгрузки: файл из нуля строк
                тоже выгрузка, и о нём надо сказать. */}
            {lastStock
              ? `${formatDayTime(lastStock)} · ${formatAmount(rows)} ${pluralRu(rows, ['строка', 'строки', 'строк'])}`
              : 'ещё не приходили'}
          </span>
        </div>
        <div>
          <span className="k">Последний чек</span>
          <span className="v num">
            {row?.lastReceipt ? formatDayTime(row.lastReceipt) : 'чеков ещё не было'}
          </span>
        </div>
      </div>
      {stale ? (
        <div className="ui-alert st-fom-alert" data-tone="warning" role="status">
          <Icon name="i-alert" />
          <div className="txt">Остатки давно не приходили, проверьте SITE.exe в аптеке</div>
        </div>
      ) : null}
    </div>
  )
}

interface FomIssuedProps {
  storeName: string
  secrets: FomSecrets
  onClose: () => void
  onToast: (message: string) => void
}

/**
 * Новые доступы — один раз. Каждое значение копируется отдельно, а «Скопировать
 * всё» собирает их одним текстом для техника FOM. Адреса — этой среды, из
 * сборки: `VITE_FOM_ORIGIN` и `VITE_FOM_PLAIN_ORIGIN`.
 */
function FomIssued({ storeName, secrets, onClose, onToast }: FomIssuedProps) {
  // Какое значение только что скопировано: его значок меняется на галочку.
  const [copied, setCopied] = useState('')
  const { origin, plainOrigin } = env.fom
  const stockUrl = origin ? `${origin}${FOM_STOCK_PATH}` : ''

  /**
   * В буфер обмена: техник вставит, а не перепишет с экрана. Буфер бывает
   * закрыт (не HTTPS, запрет браузера) — тогда значения остаются на экране.
   */
  const copy = (key: string, text: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopied(key)
      },
      () => {
        onToast('Не удалось скопировать — выделите текст и скопируйте вручную')
      },
    )
  }

  const rows: [key: string, label: string, value: string][] = [
    ['origin', 'Адрес сервиса', origin],
    ['plain', 'Без TLS, запасной', plainOrigin],
    ['login', 'Логин', secrets.tillLogin],
    ['password', 'Пароль', secrets.tillPassword],
  ]
  const site: [key: string, label: string, value: string][] = [
    ['url', '[SERVER] URL=', stockUrl],
    ['token', 'TOCING=', secrets.siteToken],
  ]

  const line = ([key, label, value]: [string, string, string]) =>
    value ? (
      <div key={key} className="st-fom-secret">
        <span className="k">{label}</span>
        <code className="ui-code">{value}</code>
        <button
          type="button"
          className="ui-iconbtn"
          aria-label={copied === key ? `${label} — скопировано` : `Скопировать: ${label}`}
          data-tip={copied === key ? 'Скопировано' : 'Скопировать'}
          onClick={() => {
            copy(key, value)
          }}
        >
          <Icon name={copied === key ? 'i-check' : 'i-copy'} />
        </button>
      </div>
    ) : null

  const all = fomHandoffText({
    storeName,
    origin,
    plainOrigin,
    tillLogin: secrets.tillLogin,
    tillPassword: secrets.tillPassword,
    siteToken: secrets.siteToken,
  })

  return (
    <Modal title="Доступы FOM" onClose={onClose} wide>
      <div className="ui-alert" data-tone="warning" role="alert">
        <Icon name="i-alert" />
        <div className="txt">
          <b>Сохраните сейчас.</b> Повторно посмотреть нельзя — только перевыпустить.
        </div>
      </div>
      {origin ? null : (
        <div className="ui-alert st-fom-alert" data-tone="danger">
          <Icon name="i-alert" />
          <div className="txt">
            Адреса API этой среды не заданы в сборке (VITE_FOM_ORIGIN) — передайте технику доступы,
            а адреса уточните у разработчиков.
          </div>
        </div>
      )}
      <div className="st-fom-group" role="group" aria-label="Касса (CASHBACK.exe)">
        <h4>Касса (CASHBACK.exe)</h4>
        {rows.map(line)}
      </div>
      <div className="st-fom-group" role="group" aria-label="SITE.exe → SETUP.ini">
        <h4>SITE.exe → SETUP.ini</h4>
        {site.map(line)}
      </div>
      <div className="mfoot">
        <button
          type="button"
          className="ui-btn"
          data-variant="secondary"
          onClick={() => {
            copy('all', all)
          }}
        >
          <Icon name={copied === 'all' ? 'i-check' : 'i-copy'} />
          {copied === 'all' ? 'Скопировано' : 'Скопировать всё одним текстом'}
        </button>
        <button type="button" className="ui-btn" onClick={onClose}>
          Сохранил, закрыть
        </button>
      </div>
    </Modal>
  )
}
